import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertTriangle, ClipboardList, Code, Copy, Files, Inbox, Pause, Pencil, Play, Plus, Trash2 } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import TableCard from '@/components/layout/TableCard'
import IndexToolbar from '@/components/IndexToolbar'
import { matchesQuery } from '@/lib/matchesQuery'
import ActionMenu from '@/components/ui/ActionMenu'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import OwnerAvatar from '@/components/OwnerAvatar'
import { useDeleteForm, useForms, useUpdateForm } from '@/features/forms/useForms'
import { copyText } from '@/features/forms/formBuilder'
import { useMembers } from '@/features/team/useTeam'
import { usePipelines } from '@/features/board/useBoard'
import { useToastStore } from '@/lib/toastStore'
import { useAuthStore } from '@/features/auth/authStore'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { formatShortDate, relativeDay } from '@/lib/dealFormat'
import { cn } from '@/lib/cn'
import type { Form } from '@/api/forms'
import { inputOf } from '@/features/forms/formBuilder'
import DuplicateFormModal from '@/features/forms/DuplicateFormModal'

/**
 * Forms: the lead-capture forms on the agency's website. A full-width table — live or paused, what each
 * creates, who gets the leads, how many came in — with each form opening in its own builder.
 */
export default function Forms() {
  const navigate = useNavigate()
  const { data: forms, isLoading } = useForms()
  const { data: members } = useMembers()
  const { data: pipelines } = usePipelines()
  const update = useUpdateForm()
  const del = useDeleteForm()
  const toast = useToastStore((s) => s.push)
  const isAdmin = useAuthStore((st) => st.user?.role === 'AGENCY_ADMIN' || st.user?.role === 'PLATFORM_ADMIN')
  const [deleting, setDeleting] = useState<Form | null>(null)
  const [duplicating, setDuplicating] = useState<Form | null>(null)
  const memberById = new Map((members ?? []).map((m) => [m.id, m]))
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<'all' | 'live' | 'paused'>('all')
  const all = forms ?? []
  const shown = all.filter(
    (f) =>
      (status === 'all' || (status === 'live') === f.enabled) &&
      matchesQuery(query, f.name, f.description, f.ownerUserId ? memberById.get(f.ownerUserId)?.fullName : null),
  )
  const pipelineName = (id: string | null) => (pipelines ?? []).find((p) => p.id === id)?.name

  async function copyLink(f: Form) {
    const ok = await copyText(`${window.location.origin}/f/${f.id}`)
    toast(ok ? `Link to “${f.name}” copied` : 'Copy isn’t available here — open the form’s Share tab', ok ? 'success' : 'error')
  }

  return (
    <PageContainer width="full">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-brand-darkest">Forms</h1>
          <p className="text-sm text-gray-500">Forms on your website — every submission becomes a contact (and a deal, if you like).</p>
        </div>
        {isAdmin && (
          <Link to="/forms/new" className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark">
            <Plus size={15} /> New form
          </Link>
        )}
      </div>

      {all.length > 0 && (
        <IndexToolbar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search forms by name, address or agent…"
          statuses={[
            { value: 'all', label: 'All', count: all.length },
            { value: 'live', label: 'Live', count: all.filter((f) => f.enabled).length },
            { value: 'paused', label: 'Paused', count: all.filter((f) => !f.enabled).length },
          ]}
          status={status}
          onStatusChange={setStatus}
          shown={shown.length}
          total={all.length}
        />
      )}
      <TableCard>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium text-gray-500 border-b border-gray-100 bg-gray-50/60">
              <th className="px-4 py-2.5">Form</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Creates</th>
              <th className="px-3 py-2.5">Leads go to</th>
              <th className="px-3 py-2.5 text-right">Submissions</th>
              <th className="px-3 py-2.5">Last submission</th>
              <th className="px-3 py-2.5 w-28"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((f) => {
              const owner = f.ownerUserId ? memberById.get(f.ownerUserId) : undefined
              return (
                <tr key={f.id} onClick={() => navigate(isAdmin ? `/forms/${f.id}` : `/forms/${f.id}?tab=submissions`)} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/80 cursor-pointer">
                  <td className="px-4 py-3 min-w-[14rem] max-w-[24rem]">
                    <Link to={`/forms/${f.id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-brand-darkest hover:text-brand-primary">
                      {f.name}
                    </Link>
                    <div className="text-xs text-gray-500">
                      {f.fields.length} field{f.fields.length === 1 ? '' : 's'}
                      {f.updatedAt ? ` · edited ${formatShortDate(f.updatedAt)}` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full', f.enabled ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500')}>
                      <span className={cn('w-1.5 h-1.5 rounded-full', f.enabled ? 'bg-success' : 'bg-gray-400')} />
                      {f.enabled ? 'Live' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {f.createDeal ? (
                      <>
                        Contact + deal
                        <div className="text-xs text-gray-500">{pipelineName(f.dealPipelineId) ?? 'Default pipeline'}</div>
                      </>
                    ) : (
                      'Contact'
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {owner ? (
                      <span className="inline-flex items-center gap-1.5 text-gray-700 whitespace-nowrap">
                        <OwnerAvatar name={owner.fullName} photoUrl={owner.photoUrl} /> {owner.fullName}
                      </span>
                    ) : (
                      <span className="text-gray-400">Unassigned</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums font-medium text-brand-darkest">{f.submissionCount ?? 0}</td>
                  <td className="px-3 py-3 text-gray-500 whitespace-nowrap">{f.lastSubmissionAt ? relativeDay(f.lastSubmissionAt) : '—'}</td>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    {isAdmin ? (
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/forms/${f.id}`} className="px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50">
                        Edit
                      </Link>
                      <ActionMenu
                        label={`Actions for ${f.name}`}
                        items={[
                          { label: 'Edit form', icon: <Pencil size={15} />, onClick: () => navigate(`/forms/${f.id}`) },
                          { label: 'Copy link', icon: <Copy size={15} />, onClick: () => void copyLink(f) },
                          { label: 'Embed code', icon: <Code size={15} />, onClick: () => navigate(`/forms/${f.id}?tab=share`) },
                          { label: 'Submissions', icon: <Inbox size={15} />, onClick: () => navigate(`/forms/${f.id}?tab=submissions`) },
                          {
                            label: 'Duplicate',
                            icon: <Files size={15} />,
                            onClick: () => setDuplicating(f),
                          },
                          f.enabled
                            ? { label: 'Pause', icon: <Pause size={15} />, onClick: () => update.mutate({ id: f.id, input: inputOf(f, { enabled: false }) }) }
                            : { label: 'Go live', icon: <Play size={15} />, onClick: () => update.mutate({ id: f.id, input: inputOf(f, { enabled: true }) }) },
                          { label: 'Delete form', icon: <Trash2 size={15} />, danger: true, onClick: () => setDeleting(f) },
                        ]}
                      />
                    </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/forms/${f.id}?tab=submissions`} className="whitespace-nowrap px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50">
                          Submissions
                        </Link>
                        <button type="button" onClick={() => void copyLink(f)} className="whitespace-nowrap px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50">
                          Copy link
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {all.length > 0 && shown.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            No forms match.{' '}
            <button type="button" onClick={() => { setQuery(''); setStatus('all') }} className="font-medium text-brand-primary hover:underline">Clear the search</button>
          </div>
        )}
        {!isLoading && (forms ?? []).length === 0 && (
          <div className="px-4 py-12 text-center">
            <ClipboardList size={28} className="mx-auto text-gray-300 mb-2" />
            {isAdmin ? (
              <>
                <p className="text-sm font-medium text-brand-darkest">No forms yet</p>
                <p className="text-sm text-gray-500 mb-3">Build a form, put it on your website, and every enquiry lands in the CRM.</p>
                <Link to="/forms/new" className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark">
                  <Plus size={15} /> Create your first form
                </Link>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-brand-darkest">No forms send leads to you yet</p>
                <p className="text-sm text-gray-500">Your admin sets up the website forms. When one sends its leads to you, it shows here with everyone who filled it in.</p>
              </>
            )}
          </div>
        )}
      </TableCard>

      {duplicating && (
        <DuplicateFormModal
          form={duplicating}
          onClose={() => setDuplicating(null)}
          onCreated={(copy) => {
            setDuplicating(null)
            navigate(`/forms/${copy.id}`)
          }}
        />
      )}

      {deleting && (
        <Modal
          title="Delete form"
          tone="danger"
          icon={<AlertTriangle size={17} />}
          size="sm"
          onClose={() => setDeleting(null)}
          footer={
            <>
              <button type="button" onClick={() => setDeleting(null)} className={modalButton.ghost}>Cancel</button>
              <button
                type="button"
                disabled={del.isPending}
                onClick={() => del.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
                className={modalButton.danger}
              >
                {del.isPending ? 'Deleting…' : 'Delete form'}
              </button>
            </>
          }
        >
          <p className="text-sm text-gray-600">
            Delete <span className="font-semibold text-brand-darkest">{deleting.name}</span>? Its link and any embed on your website stop
            working straight away. Contacts and deals it created stay.
          </p>
          {del.isError && <p className="text-danger text-xs mt-2">{apiErrorMessage(del.error)}</p>}
        </Modal>
      )}
    </PageContainer>
  )
}
